/**
 * SSN Utility Functions
 * Handles validation and formatting of Social Security Numbers
 */

/**
 * Validates and formats SSN input
 * @param value - The raw SSN input value
 * @returns Formatted SSN string
 */
export const formatSSN = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 9);

  if (digitsOnly.length === 0) {
    return '';
  }

  if (digitsOnly.length < 4) {
    return digitsOnly;
  }

  if (digitsOnly.length === 4) {
    return `xxx-xxx-${digitsOnly}`;
  }

  if (digitsOnly.length <= 5) {
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
  }

  return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5)}`;
};

/**
 * Validates SSN input
 * @param value - The SSN value to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateSSN = (value: string): { isValid: boolean; error?: string } => {
  // Remove all non-digit characters for validation
  const digitsOnly = value.replace(/\D/g, '');

  // Allow empty value (will be validated as required field elsewhere)
  if (!value.trim()) {
    return { isValid: true };
  }

  // Check if it's exactly 4 digits (last 4)
  if (digitsOnly.length === 4) {
    return { isValid: true };
  }

  // Check if it's exactly 9 digits (full SSN)
  if (digitsOnly.length === 9) {
    // Basic SSN validation rules
    const firstThree = parseInt(digitsOnly.substring(0, 3));
    const middleTwo = parseInt(digitsOnly.substring(3, 5));
    const lastFour = parseInt(digitsOnly.substring(5, 9));

    // SSN cannot be all zeros in any section
    if (firstThree === 0 || middleTwo === 0 || lastFour === 0) {
      return { isValid: false, error: 'Invalid SSN format' };
    }

    // SSN cannot start with 666 or 9
    if (firstThree === 666 || firstThree >= 900) {
      return { isValid: false, error: 'Invalid SSN format' };
    }

    return { isValid: true };
  }

  // If not 4 or 9 digits, it's invalid
  return {
    isValid: false,
    error: 'SSN must be either 4 digits (last 4) or 9 digits (full SSN)'
  };
};

/**
 * Extracts the last 4 digits from a formatted SSN
 * @param value - The formatted SSN value
 * @returns Last 4 digits as string
 */
export const getLastFourDigits = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');

  if (digitsOnly.length >= 4) {
    return digitsOnly.substring(digitsOnly.length - 4);
  }

  return digitsOnly;
};

/**
 * Checks if the SSN is a full 9-digit SSN
 * @param value - The SSN value to check
 * @returns True if it's a full 9-digit SSN
 */
export const isFullSSN = (value: string): boolean => {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.length === 9;
};
